// Birim varyantı — katalog metriğinin "Alternatif birimler" alanı (MetricModal). `UnitCombobox`
// (aile başlıklı, yazarak süzülen liste) + görünür "Ekle"; Enter de ekler, form gönderilmez.
// Her alternatifin yanında varsayılanla katsayı ("1 GJ = 277,8 kWh") ya da "çevrilmez"; tanınmayan
// birim tek amber satır. Varsayılanla aynısı ve listede olan (normalize anahtar) reddedilir + not.
// Ekleme kararı saf `addUnitVariant` — bileşen ve testler aynısını kullanır (DOM ortamı yok).

import { useId, useState, type CSSProperties } from "react";
import { isSelectableUnit, normalizeUnitKey } from "@mahir/shared";
import { KPI_UNIT_COPY } from "../../lib/kpi-unit-copy";
import { unitRelation } from "../../lib/kpi-unit-picker";
import {
  amberBox,
  fieldStyle,
  helpText,
  hitArea44,
  hitArea44Visual,
  labelStyle,
} from "./kpi-form-styles";
import { UnitCombobox } from "./UnitCombobox";

const V = KPI_UNIT_COPY.variantsField;
export const MAX_UNIT_VARIANTS = 20;

export type UnitVariantNote = "sameAsDefault" | "duplicate" | "limit" | null;

export function addUnitVariant(
  list: readonly string[],
  raw: string,
  defaultUnit: string,
): { list: string[]; note: UnitVariantNote; added: boolean } {
  const u = raw.trim();
  if (u === "") return { list: [...list], note: null, added: false };
  const key = normalizeUnitKey(u);
  if (defaultUnit.trim() !== "" && normalizeUnitKey(defaultUnit) === key) {
    return { list: [...list], note: "sameAsDefault", added: false };
  }
  if (list.some((x) => normalizeUnitKey(x) === key)) {
    return { list: [...list], note: "duplicate", added: false };
  }
  if (list.length >= MAX_UNIT_VARIANTS) return { list: [...list], note: "limit", added: false };
  return { list: [...list, u], note: null, added: true };
}

export function unitVariantNoteText(note: UnitVariantNote): string | null {
  if (note === "sameAsDefault") return V.sameAsDefault;
  if (note === "duplicate") return V.duplicate;
  if (note === "limit") return V.limit(MAX_UNIT_VARIANTS);
  return null;
}

export interface UnitVariantsFieldProps {
  value: readonly string[];
  defaultUnit: string;
  disabled?: boolean;
  onChange: (next: string[]) => void;
}

export function UnitVariantsField(props: UnitVariantsFieldProps): JSX.Element {
  const { value, defaultUnit, disabled, onChange } = props;
  const id = useId();
  const [text, setText] = useState("");
  const [note, setNote] = useState<UnitVariantNote>(null);
  // Tanınmayan birim uyarısı TEK satır (chip başına tekrar yok): kalıcı listeden türer
  const unknownUnits = value.filter((u) => !isSelectableUnit(u));
  const noteText = unitVariantNoteText(note);

  function commit(raw: string): void {
    const r = addUnitVariant(value, raw, defaultUnit);
    setNote(r.note);
    if (r.added) {
      onChange(r.list);
      setText("");
    }
  }

  return (
    <div style={fieldStyle}>
      <label style={labelStyle} htmlFor={id}>
        {V.label}
      </label>
      <span style={helpText}>{V.help}</span>
      {value.length > 0 ? (
        <ul aria-label={V.label} style={chipList}>
          {value.map((u) => {
            const known = isSelectableUnit(u);
            const rel = unitRelation(defaultUnit, u);
            return (
              <li key={u} style={row}>
                <span style={known ? chip : { ...chip, ...chipUnknown }}>
                  {known ? u : `${u} ⚠`}
                </span>
                {rel.kind === "factor" ? <span style={relText}>{rel.text}</span> : null}
                {rel.kind === "noConversion" ? (
                  <span style={{ ...relText, color: "var(--color-warning-text)" }}>
                    ⚠ {V.noConversion}
                  </span>
                ) : null}
                {disabled ? null : (
                  <button
                    type="button"
                    aria-label={V.remove(u)}
                    style={hitArea44}
                    onClick={() => onChange(value.filter((x) => x !== u))}
                  >
                    <span aria-hidden="true" style={hitArea44Visual}>
                      ×
                    </span>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      {unknownUnits.length > 0 ? (
        <div
          role="status"
          style={{ ...amberBox, fontSize: "0.875rem", padding: "4px 10px", width: "fit-content" }}
        >
          ⚠ {V.unknownList(unknownUnits.join(", "))}
        </div>
      ) : null}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <UnitCombobox
          id={id}
          ariaLabel={V.label}
          value={text}
          onChange={(t) => {
            setText(t);
            if (note !== null) setNote(null);
          }}
          onCommit={commit}
          related={defaultUnit}
          exclude={value}
          placeholder={V.placeholder}
          {...(disabled ? { disabled } : {})}
        />
        <button
          type="button"
          disabled={(disabled ?? false) || text.trim() === ""}
          style={{
            ...btnAdd,
            opacity: (disabled ?? false) || text.trim() === "" ? 0.5 : 1,
          }}
          onClick={() => commit(text)}
        >
          {V.add}
        </button>
      </div>
      {noteText ? (
        <div
          role="status"
          style={{ ...amberBox, fontSize: "0.875rem", padding: "4px 10px", width: "fit-content" }}
        >
          ⚠ {noteText}
        </div>
      ) : null}
    </div>
  );
}

const chipList: CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
};
const chip: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "2px 10px",
  borderRadius: 999,
  border: "1px solid var(--color-border)",
  background: "color-mix(in srgb, var(--color-primary) 8%, white)",
  color: "var(--color-text)",
  fontFamily: "var(--font-sans)",
  fontSize: "0.875rem",
  minHeight: 32,
};
const chipUnknown: CSSProperties = {
  background: "var(--color-warning-bg)",
  borderColor: "var(--color-warning-border)",
  color: "var(--color-warning-text)",
};
const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
};
const relText: CSSProperties = {
  ...helpText,
  fontFamily: "var(--font-sans)",
  fontSize: "0.875rem",
};
const btnAdd: CSSProperties = {
  minHeight: 44,
  minWidth: 44,
  padding: "0 16px",
  borderRadius: 8,
  border: "1px solid var(--color-primary)",
  background: "transparent",
  color: "var(--color-primary-text, var(--color-primary))",
  fontFamily: "var(--font-sans)",
  fontSize: "1rem",
  fontWeight: 600,
  cursor: "pointer",
};
